// Service catalog ported from prototype/src/data.jsx (SERVICE_GROUPS + SERVICES).
// Used by: server `/api/services`, the public marketing site, and the portal's
// "new request" wizard. Phase 3 (CMS) will make these editable; for Phase 1
// they're static, single-source-of-truth here.

import { z } from 'zod';

export const ServiceGroupSchema = z.object({
  id: z.string(),
  label: z.string(),
  blurb: z.string(),
});
export type ServiceGroup = z.infer<typeof ServiceGroupSchema>;

export const ServiceSchema = z.object({
  id: z.string(),
  group: z.string(),
  name: z.string(),
  long: z.string(),
  fee: z.string(),
  lead: z.string(),
  summary: z.string(),
  detail: z.array(z.string()),
  not_regulated: z.string(),
});
export type Service = z.infer<typeof ServiceSchema>;

export const SERVICE_GROUPS: ServiceGroup[] = [
  { id: 'instruments', label: 'Trade Instruments',         blurb: 'Document preparation & introductions for international trade instruments between corporate entities.' },
  { id: 'capital',     label: 'Capital Introductions',     blurb: 'Corporate finance introduction services for sophisticated body corporates seeking capital.' },
  { id: 'projects',    label: 'Projects & Infrastructure', blurb: 'Strategic consultancy and feasibility studies for large-scale commercial projects.' },
  { id: 'research',    label: 'Research & Intelligence',   blurb: 'Market intelligence, AI-driven analysis and corporate due diligence reports.' },
  { id: 'corporate',   label: 'Corporate Services',        blurb: 'Structuring, compliance packs and roadshow preparation for corporates expanding cross-border.' },
];

// Verbatim from prototype/src/data.jsx — see that file for line attribution.
export const SERVICES: Service[] = [
  // ── Trade Instruments ──────────────────────────────────────────────
  {
    id: 'sblc', group: 'instruments',
    name: 'SBLC Facilitation',
    long: 'Standby Letter of Credit',
    fee: 'From 2% facilitation fee', lead: '4–8 weeks',
    summary: 'Administrative support and document preparation for SBLC issuance between corporate entities through authorised banking partners.',
    detail: [
      'Drafting and review of MT760 / MT799 application packs.',
      'Introduction to authorised issuing institutions in the UK, EU and GCC.',
      'KYC/AML pack preparation aligned with the issuing bank’s onboarding.',
      'Coordination of communication between applicant and beneficiary counsel.',
    ],
    not_regulated: 'We do not issue, broker or guarantee instruments. The instrument is issued by an FCA- or equivalently authorised institution.',
  },
  {
    id: 'bg', group: 'instruments',
    name: 'Bank Guarantee Support',
    long: 'Bank Guarantee Document Preparation',
    fee: 'From 2% facilitation fee', lead: '3–6 weeks',
    summary: 'Document preparation and corporate introductions for Bank Guarantees (BG) used in performance, payment and tender contexts.',
    detail: [
      'Tender, advance-payment and performance BG document drafting.',
      'Counter-guarantee structuring research between issuing and confirming banks.',
      'Introductions to authorised banks for the corporate applicant.',
      'Liaison with corporate counsel; we do not provide legal advice.',
    ],
    not_regulated: 'Issuance is undertaken solely by authorised banking institutions. Omega prepares paperwork and makes introductions only.',
  },
  {
    id: 'tradefin', group: 'instruments',
    name: 'Trade Finance Facilitation',
    long: 'LC, DLC & Proof of Funds',
    fee: 'From 2% facilitation fee', lead: '2–4 weeks',
    summary: 'Documentary Letters of Credit (LC/DLC), Proof of Funds (POF) and Ready-Willing-Able verification packs for cross-border trade.',
    detail: [
      'Preparation of LC/DLC application packs aligned with UCP 600.',
      'POF / RWA letter document preparation and authentication coordination.',
      'Introductions to commodity-experienced authorised banking partners.',
    ],
    not_regulated: 'Verification, authentication and issuance is performed exclusively by authorised institutions.',
  },
  {
    id: 'monetisation', group: 'instruments',
    name: 'Instrument Monetisation Introductions',
    long: 'Authorised monetisation partner referrals',
    fee: 'From 2% + success-based', lead: '4–10 weeks',
    summary: 'Introductions to FCA- or equivalently authorised monetisation desks where a corporate beneficiary holds an eligible instrument.',
    detail: [
      'Eligibility screening of the corporate holder and instrument paperwork.',
      'Introduction to authorised monetisation counterparties.',
      'No client funds are ever held by Omega. No advice on merits is given.',
    ],
    not_regulated: 'Pure RAO Article 15 introducer activity — client engages directly with the authorised partner.',
  },
  {
    id: 'escrow', group: 'instruments',
    name: 'Escrow Introductions',
    long: 'Authorised escrow agent referrals',
    fee: 'From 2% flat introduction', lead: '1–2 weeks',
    summary: 'Introductions to authorised escrow agents (UK solicitors, regulated trust companies) for closings, deposits and milestone payments.',
    detail: [
      'Brief preparation summarising the transaction and escrow scope.',
      'Shortlist of authorised escrow agents matched on jurisdiction and ticket size.',
      'No client money flows through Omega at any point.',
    ],
    not_regulated: 'Holding client money is heavily regulated. We refer; we never hold.',
  },

  // ── Capital Introductions ───────────────────────────────────────────
  {
    id: 'private-funding', group: 'capital',
    name: 'Private Funding Introductions',
    long: 'Corporate Finance Introductions',
    fee: 'From 2% + success-based', lead: '8–16 weeks',
    summary: 'Corporate finance introduction services connecting sophisticated corporate borrowers with authorised private credit and family-office capital.',
    detail: [
      'Capital narrative document preparation (information memorandum framework).',
      'Introductions to authorised private credit funds, family offices and merchant banks.',
      'Coordination of follow-up calls; no advice on the merits is provided.',
    ],
    not_regulated: 'Article 29 framework — introducing to authorised persons, no advice, no client money.',
  },
  {
    id: 'venture', group: 'capital',
    name: 'Venture Capital Introductions',
    long: 'Sophisticated VC introductions',
    fee: 'From 2% + success-based', lead: '12–20 weeks',
    summary: 'Introductions to authorised venture and growth-stage funds for sophisticated corporate issuers raising equity.',
    detail: [
      'Investor map building and warm introduction sequencing.',
      'Pitch-deck and data-room readiness review.',
      'Available only to body corporates that self-certify as sophisticated.',
    ],
    not_regulated: 'No promotion of unauthorised collective investment schemes; communications restricted to s.21 FSMA exempt categories.',
  },
  {
    id: 'hedge', group: 'capital',
    name: 'Hedge Fund Research',
    long: 'Performance & strategy analytical reports',
    fee: 'From 2% / subscription', lead: 'Reports in 5 days',
    summary: 'Independent analytical reporting on hedge-fund strategies, performance and operational due diligence — for corporate research use only.',
    detail: [
      'Strategy mapping (long/short, macro, multi-strat, quant) with risk lenses.',
      'Returns and drawdown analysis on public data only.',
      'Operational red-flag screening framework.',
    ],
    not_regulated: 'Research is informational only. We do not promote any specific fund or solicit investment.',
  },
  {
    id: 'ppp', group: 'capital',
    name: 'Private Placement Programme Research',
    long: 'PPP analytical reporting',
    fee: 'From 2% per engagement', lead: '2–3 weeks',
    summary: 'Analytical research and corporate education on the structures, risks and authorised counterparties involved in private placement programmes.',
    detail: [
      'Structural overview of compliant placement frameworks.',
      'Counterparty due-diligence reports.',
      'Education materials — not solicitation.',
    ],
    not_regulated: 'PPPs are a high-risk area subject to extensive FCA warnings. Omega provides research and education only and does not arrange, promote or transact.',
  },
  {
    id: 'family-office', group: 'capital',
    name: 'Family Office Introductions',
    long: 'HNW corporate introductions',
    fee: 'From 2% + success-based', lead: '6–12 weeks',
    summary: 'Curated introductions to UK, European and GCC family offices and HNW corporate vehicles for co-investment dialogue.',
    detail: [
      'Mandate brief preparation in family-office friendly format.',
      'Direct warm introductions; no cold outreach in the client’s name.',
      'Restricted to body corporates that self-certify under FPO exemptions.',
    ],
    not_regulated: 'Communications shaped to fall within Financial Promotion Order exempt categories.',
  },

  // ── Projects & Infrastructure ────────────────────────────────────────
  {
    id: 'project-dev', group: 'projects',
    name: 'Project Development Consultancy',
    long: 'Strategic project development advisory',
    fee: 'From 2% / fixed-scope', lead: 'From 4 weeks',
    summary: 'Strategic consultancy supporting corporate clients through the development phases of large-scale infrastructure and commercial projects.',
    detail: [
      'Concept-to-bankability roadmap.',
      'Stakeholder mapping across government, EPC and offtake counterparties.',
      'Risk register and mitigation framework.',
    ],
    not_regulated: 'Pure management consultancy.',
  },
  {
    id: 'project-fin', group: 'projects',
    name: 'Project Finance Feasibility',
    long: 'Feasibility studies and capital structuring research',
    fee: 'From 2% / fixed-scope', lead: '6–12 weeks',
    summary: 'Feasibility studies and capital-structure research for corporate sponsors approaching authorised project lenders.',
    detail: [
      'Cashflow modelling and sensitivity scenarios.',
      'Sources-and-uses framework for sponsor discussions with authorised lenders.',
      'Term-sheet negotiation preparation — client signs with the authorised lender, not Omega.',
    ],
    not_regulated: 'Modelling and feasibility research; we do not arrange or advise on regulated debt.',
  },
  {
    id: 'infrastructure', group: 'projects',
    name: 'Infrastructure Advisory',
    long: 'Energy, transport, social infrastructure',
    fee: 'From 2% / fixed-scope', lead: '8–16 weeks',
    summary: 'Sector-specialist consultancy across energy, transport, water and social-infrastructure projects in the UK, GCC, India and SE Asia.',
    detail: [
      'PPP / concession structure benchmarking research.',
      'Permitting and stakeholder navigation playbook.',
      'Long-list of authorised lenders and EPC partners.',
    ],
    not_regulated: 'Strategic consultancy.',
  },
  {
    id: 'real-estate', group: 'projects',
    name: 'Real Estate Project Advisory',
    long: 'Mixed-use, hospitality and residential',
    fee: 'From 2% / fixed-scope', lead: '6–10 weeks',
    summary: 'Feasibility and positioning research for mixed-use, hospitality and large residential developments.',
    detail: [
      'Catchment and demand research.',
      'Competitor benchmark and pricing strategy research.',
      'Introductions to authorised real-estate debt funds.',
    ],
    not_regulated: 'Research and consultancy only.',
  },
  {
    id: 'esg', group: 'projects',
    name: 'ESG & Carbon Credit Advisory',
    long: 'Project-level sustainability research',
    fee: 'From 2% / fixed-scope', lead: '4–8 weeks',
    summary: 'Project-level ESG diagnostics and carbon-credit pathway research for corporates structuring sustainable projects.',
    detail: [
      'Standards mapping (Verra, Gold Standard, ICVCM).',
      'MRV (measure / report / verify) framework research.',
      'Introductions to authorised verification bodies.',
    ],
    not_regulated: 'Research and consultancy.',
  },

  // ── Research & Intelligence ──────────────────────────────────────────
  {
    id: 'market-research', group: 'research',
    name: 'Market Research',
    long: 'Sector and market intelligence',
    fee: 'From 2% per report', lead: '5–15 days',
    summary: 'Comprehensive market intelligence tailored to UK, EU, UAE, India and SE Asia sectors enabling informed corporate decisions.',
    detail: [
      'Sizing, segmentation and competitive landscape.',
      'Channel and pricing benchmarks.',
      'Custom data collection on request.',
    ],
    not_regulated: 'Already unregulated.',
  },
  {
    id: 'ai-research', group: 'research',
    name: 'AI Research & Analytics',
    long: 'Applied AI and quantitative trend analysis',
    fee: 'From 2% per engagement', lead: '2–6 weeks',
    summary: 'Data-driven market intelligence powered by applied AI — trend detection, news pipelines, and quantitative scoring.',
    detail: [
      'Custom news / filings ingestion pipelines.',
      'Trend detection and signal monitoring dashboards.',
      'Model evaluation and audit reports.',
    ],
    not_regulated: 'Information service. We do not provide investment recommendations.',
  },
  {
    id: 'due-diligence', group: 'research',
    name: 'Due Diligence Reports',
    long: 'Corporate intelligence & background checks',
    fee: 'From 2% per report', lead: '7–14 days',
    summary: 'Corporate intelligence and integrity due-diligence reports across counterparties, sponsors and beneficial owners.',
    detail: [
      'Open-source intelligence (OSINT) and registry sweep.',
      'Sanctions, PEP and adverse-media screening.',
      'Structured Level 1 / 2 / 3 reporting tiers.',
    ],
    not_regulated: 'Corporate intelligence.',
  },

  // ── Corporate Services ───────────────────────────────────────────────
  {
    id: 'kyc-aml', group: 'corporate',
    name: 'KYC / AML Compliance Packs',
    long: 'Onboarding-ready compliance documentation',
    fee: 'From 2% / fixed-fee pack', lead: '5–10 days',
    summary: 'Onboarding-ready KYC/AML packs that meet the documentary expectations of UK and GCC banking and authorised counterparties.',
    detail: [
      'Beneficial ownership map and source-of-wealth narrative.',
      'Sanctions, PEP and adverse-media checks.',
      'Renewal calendar and refresh service.',
    ],
    not_regulated: 'We are subject to AML obligations as a TCSP/relevant business; this is a professional service, not financial advice.',
  },
  {
    id: 'structuring', group: 'corporate',
    name: 'Corporate Structuring',
    long: 'UK / UAE / offshore vehicle setup research',
    fee: 'From 2% / fixed-scope', lead: '3–6 weeks',
    summary: 'Comparative research on UK, UAE freezone/mainland and recognised offshore corporate vehicles for cross-border activity.',
    detail: [
      'Comparative tax-and-substance research with authorised tax counsel.',
      'Vehicle registration coordination via authorised agents.',
      'Bank-account onboarding research.',
    ],
    not_regulated: 'Research and coordination. Tax advice provided by authorised counsel.',
  },
  {
    id: 'roadshow', group: 'corporate',
    name: 'Investor Roadshow & Pitch Prep',
    long: 'Decks, data-rooms and rehearsal',
    fee: 'From 2% / fixed-scope', lead: '2–4 weeks',
    summary: 'Pitch-deck, data-room and rehearsal support for corporate teams approaching authorised investors.',
    detail: [
      'Narrative architecture and deck design.',
      'Q&A bank and rehearsal calls.',
      'Data-room curation and access logging.',
    ],
    not_regulated: 'Marketing and communications support; no investment advice.',
  },
  {
    id: 'commodities', group: 'corporate',
    name: 'Commodities Trade Introductions',
    long: 'Buyer / seller corporate introductions',
    fee: 'From 2% per introduction', lead: '4–10 weeks',
    summary: 'Curated introductions between verified corporate buyers and sellers across metals, energy and agri-commodities.',
    detail: [
      'KYC verification of both counterparties before introduction.',
      'Structured Procedure documentation support.',
      'No participation in the commercial transaction.',
    ],
    not_regulated: 'Pure introducer activity. The commercial contract sits between the corporates.',
  },
];

// ── Geographies + headline stats (homepage + about page) ───────────────
export const GEOGRAPHIES = [
  { code: 'UK',  label: 'United Kingdom',     city: 'London (HQ)' },
  { code: 'AE',  label: 'United Arab Emirates', city: 'Dubai' },
  { code: 'IN',  label: 'India',              city: 'India' },
  { code: 'EU',  label: 'European Union',     city: 'via partners' },
  { code: 'SEA', label: 'Southeast Asia',     city: 'via partners' },
  { code: 'NA',  label: 'North America',      city: 'via partners' },
] as const;

export const HEADLINE_STATS = [
  { k: '£840m', v: 'Capital introduced via authorised partners (lifetime)' },
  { k: '22',    v: 'Specialist B2B service lines' },
  { k: '6',     v: 'Geographies covered directly or via partners' },
  { k: '100%',  v: 'Body-corporate clients only' },
] as const;
